// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Star, Users, Video } from 'lucide-react';
import { toast } from 'sonner';

interface VideoVendor {
  id: string;
  business_name: string;
  description?: string;
  rating_average?: number;
  review_count?: number;
  user_id: string;
}

interface VideoVendorSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventName: string;
  onVendorSelected: (vendor: VideoVendor) => void;
}

const VideoVendorSelectionModal: React.FC<VideoVendorSelectionModalProps> = ({
  open,
  onOpenChange,
  eventId,
  eventName,
  onVendorSelected,
}) => {
  const [vendors, setVendors] = useState<VideoVendor[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && eventId) {
      fetchAvailableVendors();
    }
  }, [open, eventId]);

  const fetchAvailableVendors = async () => {
    setLoading(true);
    try {
      // Query vendor_event_availability joined with video_vendor_profiles
      const { data, error } = await supabase
        .from('vendor_event_availability')
        .select(`
          vendor_id,
          video_vendor_profiles!inner (
            id,
            business_name,
            description,
            rating_average,
            review_count,
            user_id
          )
        `)
        .eq('event_id', eventId);

      if (error) throw error;

      // Transform the data to extract vendor profiles
      const availableVendors: VideoVendor[] = (data || [])
        .map(item => {
          const profile = item.video_vendor_profiles as any;
          if (profile) {
            return {
              id: profile.id,
              business_name: profile.business_name,
              description: profile.description,
              rating_average: profile.rating_average,
              review_count: profile.review_count,
              user_id: profile.user_id,
            } as VideoVendor;
          }
          return null;
        })
        .filter((vendor): vendor is VideoVendor => vendor !== null);

      setVendors(availableVendors);
    } catch (error) {
      console.error('Error fetching available vendors:', error);
      toast.error('Failed to load available video vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVendor = (vendor: VideoVendor) => {
    onVendorSelected(vendor);
    onOpenChange(false);
  };

  const renderStarRating = (rating: number, reviewCount: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star key={i} className="h-4 w-4 fill-yellow-400/50 text-yellow-400" />
        );
      } else {
        stars.push(
          <Star key={i} className="h-4 w-4 text-gray-300" />
        );
      }
    }

    return (
      <div className="flex items-center gap-1">
        <div className="flex">{stars}</div>
        <span className="text-sm text-muted-foreground">
          {rating.toFixed(1)} ({reviewCount} reviews)
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Select Video Vendor
          </DialogTitle>
          <DialogDescription>
            Choose a video vendor for <strong>{eventName}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading available video vendors...</span>
          </div>
        ) : vendors.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {vendors.map((vendor) => (
              <Card key={vendor.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{vendor.business_name}</CardTitle>
                      {vendor.rating_average !== undefined && vendor.review_count !== undefined && (
                        <div className="mt-2">
                          {renderStarRating(vendor.rating_average, vendor.review_count)}
                        </div>
                      )}
                    </div>
                    <Badge variant="secondary" className="ml-2">
                      Video Vendor
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {vendor.description && (
                    <CardDescription className="text-sm leading-relaxed">
                      {vendor.description}
                    </CardDescription>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {vendor.review_count !== undefined && (
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{vendor.review_count} reviews</span>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full"
                    onClick={() => handleSelectVendor(vendor)}
                  >
                    Select Vendor
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Video className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Video Vendors Available</h3>
            <p className="text-muted-foreground">
              No video vendors are currently available for this event. Please check back later.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoVendorSelectionModal;